// src/pages/ProductMonAppliMairie/MonAppliMairie.jsx
import React, { useEffect, useMemo, useState } from "react";
import Footer from "../Home/Footer";
import useScrollToTop from "../../hooks/useScrollToTop";
import AuthIcons from "../../hooks/AuthIcons";
import { useUser } from "../../contexts/UserContext";
import FormContact from "../Home/FormContact";
import LandingButton from "./../../components/Button/LandingButton.jsx";

import DemoFeatureTourSection from "./DemoFeatureTourSection";
import DemoTutorialSection from "./DemoTutorialSection";
import DemoPricingSection from "./DemoPricingSection";
import DemoAcquisitionSection from "./DemoAcquisitionSection";

import {
  FaArrowRight,
  FaPhoneAlt,
  FaGlobe,
  FaUsers,
  FaCalendarAlt,
  FaFileAlt,
  FaLandmark,
  FaCheckCircle,
} from "react-icons/fa";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const FeatureCard = ({ title, items, accent, icon: Icon }) => (
  <div
    className={cx(
      "rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg",
      "dark:border-white/10 dark:bg-slate-900",
    )}
  >
    <div className="flex items-start gap-3">
      <div
        className={cx(
          "grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-white",
          "bg-gradient-to-r",
          accent,
        )}
      >
        <Icon />
      </div>

      <div>
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </div>
        <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Fonctions essentielles.
        </div>
      </div>
    </div>

    <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-200">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <FaCheckCircle className="mt-0.5 text-emerald-500" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AboutMonAppliMairie = () => {
  const blocks = useMemo(
    () => [
      {
        title: "Communication communale",
        icon: FaGlobe,
        accent: "from-sky-500 to-cyan-500",
        items: [
          "Page d’accueil moderne et administrable",
          "Actualités, agenda et informations prioritaires",
          "Valorisation de la vie locale et du territoire",
          "Navigation claire pour les habitants et visiteurs",
        ],
      },
      {
        title: "Services & administration",
        icon: FaLandmark,
        accent: "from-indigo-500 to-violet-600",
        items: [
          "Présentation des élus et services municipaux",
          "Publications, documents et contenus téléchargeables",
          "Création de pages via CMS intégré",
          "Organisation simple des menus et rubriques",
        ],
      },
      {
        title: "Vie locale",
        icon: FaUsers,
        accent: "from-emerald-500 to-green-600",
        items: [
          "Mise en avant des associations locales",
          "Annuaire des commerces et artisans",
          "Pages dédiées aux projets de la commune",
          "Carte interactive et informations utiles",
        ],
      },
      {
        title: "Accompagnement humain",
        icon: FaCalendarAlt,
        accent: "from-amber-500 to-orange-500",
        items: [
          "Paramétrage initial de votre commune",
          "Formation des agents et élus",
          "Mise en ligne rapide",
          "Support direct par téléphone ou mail",
        ],
      },
    ],
    [],
  );

  return (
    <section
      id="mapm-about"
      className="bg-white py-16 sm:py-20 dark:bg-slate-950"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-6 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-7">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Vue d’ensemble
              </div>

              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
                Un site communal moderne, clair et simple à administrer
              </h2>

              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                MonAppliMairie permet à une commune de disposer d’un site web
                professionnel pour informer les habitants, valoriser la vie
                locale et centraliser les contenus essentiels dans une interface
                simple à prendre en main.
              </p>

              <div className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                <img
                  src="/images/diaporama_monapplimairie/006.png"
                  alt="Back-office MonAppliMairie"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="mt-6 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                {[
                  "Site responsive : mobile, tablette et PC",
                  "Administration pensée pour les agents municipaux",
                  "Accompagnement au démarrage et dans la durée",
                ].map((item) => (
                  <div key={item} className="flex gap-3">
                    <FaCheckCircle className="mt-0.5 text-sky-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <LandingButton
                  variant="primary"
                  onClick={() => scrollToId("fonctionnalites")}
                >
                  Découvrir les fonctionnalités
                </LandingButton>

                <LandingButton
                  variant="secondary"
                  onClick={() => scrollToId("tarifs")}
                >
                  Voir les tarifs
                </LandingButton>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Pourquoi MonAppliMairie ?
              </div>
              <h3 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
                Une solution complète pour présenter la commune et publier
                l’information utile
              </h3>
              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                Le site public et son back-office ont été pensés pour aller à
                l’essentiel : publier facilement, structurer les contenus,
                rendre l’information accessible et donner une image moderne de
                la commune.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              {blocks.map((block) => (
                <FeatureCard
                  key={block.title}
                  title={block.title}
                  items={block.items}
                  accent={block.accent}
                  icon={block.icon}
                />
              ))}
            </div>

            <div className="mt-6 overflow-hidden rounded-[32px] border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
              <img
                src="/images/diaporama_monapplimairie/007.png"
                alt="Aperçu page d’accueil MonAppliMairie"
                className="w-full rounded-[24px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MonAppliMairie = () => {
  useScrollToTop();
  const { user } = useUser();

  const heroImages = useMemo(
    () => [
      "/images/diaporama_monapplimairie/001.png",
      "/images/diaporama_monapplimairie/002.png",
      "/images/diaporama_monapplimairie/003.png",
      "/images/diaporama_monapplimairie/004.png",
    ],
    [],
  );

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reduceMotion) return;

    const interval = setInterval(() => {
      setIdx((prev) => (prev + 1) % heroImages.length);
    }, 5500);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const quickLinks = [
    { id: "mapm-about", label: "Vue d’ensemble" },
    { id: "fonctionnalites", label: "Fonctionnalités" },
    // { id: "tutoriel", label: "Aperçus" },
    { id: "tarifs", label: "Tarifs" },
    { id: "presentation", label: "Mise en place" },
    { id: "contact-general", label: "Contact" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-slate-950">
      {!user && <AuthIcons />}

      <main className="flex-grow">
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-slate-200 bg-[#eef6ff] dark:border-white/10 dark:bg-slate-950">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.12),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.12),transparent_28%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.12),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.12),transparent_28%)]" />
          <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-sky-200/20 blur-3xl dark:bg-sky-500/10" />

          <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 sm:pb-20 sm:pt-28 lg:px-8 lg:pb-24 lg:pt-32">
            <div className="grid items-center gap-14 lg:grid-cols-2">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-medium text-sky-700 shadow-sm dark:border-sky-500/20 dark:bg-slate-900 dark:text-sky-300">
                  <span className="inline-block h-2 w-2 rounded-full bg-sky-500" />
                  MonAppliMairie
                </div>

                <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl dark:text-white">
                  Un site communal moderne pour
                  <span className="block text-sky-600 dark:text-sky-400">
                    informer, valoriser et administrer
                  </span>
                </h1>

                <p className="mt-6 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                  MonAppliMairie aide les communes à publier leurs actualités,
                  événements, documents, services, associations, commerces et
                  projets dans un site clair, moderne et simple à piloter.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <LandingButton
                    variant="primary"
                    onClick={() => scrollToId("fonctionnalites")}
                  >
                    Découvrir les fonctionnalités
                  </LandingButton>

                  <LandingButton
                    variant="secondary"
                    onClick={() => scrollToId("tutoriel")}
                  >
                    Voir quelques écrans
                  </LandingButton>

                  <LandingButton
                    variant="secondary"
                    onClick={() => scrollToId("tarifs")}
                  >
                    Demander un devis
                  </LandingButton>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {[
                    "Site communal moderne",
                    "Responsive mobile / tablette / PC",
                    "Administration simple",
                    "Accompagnement humain",
                  ].map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Mockup */}
              <div className="relative">
                <div className="absolute -left-4 top-10 hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900 lg:block">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-sky-100 p-3 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400">
                      <FaGlobe />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                        Communication
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white">
                        Information locale claire
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-4 bottom-10 hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900 lg:block">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-emerald-100 p-3 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                      <FaUsers />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                        Public
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white">
                        Habitants & visiteurs
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_30px_80px_-20px_rgba(15,23,42,0.20)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]">
                  <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-slate-200 dark:bg-slate-700" />
                      <span className="h-3 w-3 rounded-full bg-slate-200 dark:bg-slate-700" />
                      <span className="h-3 w-3 rounded-full bg-slate-200 dark:bg-slate-700" />
                    </div>
                    <div className="rounded-full bg-slate-100 px-4 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Aperçu MonAppliMairie
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-800/70">
                      <img
                        src={heroImages[idx]}
                        alt="Aperçu MonAppliMairie"
                        className="h-[260px] w-full object-cover sm:h-[340px]"
                        loading="lazy"
                      />
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-4">
                      <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/80">
                        <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          Site communal
                        </div>
                        <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                          Moderne
                        </div>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/80">
                        <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          Administration
                        </div>
                        <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                          Simplifiée
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 rounded-3xl bg-gradient-to-br from-sky-500 via-cyan-500 to-indigo-500 p-5 text-white">
                      <div className="text-sm font-medium text-white/80">
                        Ce que gagne votre commune
                      </div>
                      <div className="mt-2 text-2xl font-bold sm:text-3xl">
                        Informer les habitants.
                        <br />
                        Valoriser la commune.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex justify-center gap-2">
                  {heroImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={cx(
                        "h-2.5 rounded-full transition-all",
                        i === idx
                          ? "w-10 bg-sky-500"
                          : "w-2.5 bg-slate-300 dark:bg-slate-700",
                      )}
                      aria-label={`Image ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUICK NAV */}
        <section className="sticky top-20 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/85">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-2 py-4">
              {quickLinks.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToId(item.id)}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-16 bg-slate-50 dark:bg-slate-900/40">
          <img
            src="/images/diaporama_monapplimairie/illustration_monapplimairie.webp"
            alt="Illustration MonAppliMairie"
            className="max-h-[620px] w-full object-cover"
            loading="lazy"
          />
          <div className="border-t border-slate-200 dark:border-white/10" />
        </div>

        <AboutMonAppliMairie />

        <DemoFeatureTourSection />

        {/* <DemoTutorialSection /> */}

        <DemoPricingSection />

        <DemoAcquisitionSection />

        {/* CTA */}
        {/* <section className="border-y border-slate-200 bg-slate-50 py-16 dark:border-white/10 dark:bg-slate-900/40">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
              Passer à l’action
            </div>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
              Envie d’un site communal clair, moderne et facile à gérer ?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
              Découvrons ensemble la formule adaptée à votre commune et mettons
              en ligne votre site rapidement.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <LandingButton
                variant="primary"
                onClick={() => scrollToId("tarifs")}
              >
                Voir les tarifs <FaArrowRight className="text-sm" />
              </LandingButton>

              <LandingButton
                variant="secondary"
                onClick={() => scrollToId("presentation")}
              >
                Comprendre la mise en place
              </LandingButton>

              <a
                href="tel:0680727707"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:hover:bg-slate-800"
              >
                <FaPhoneAlt /> 06.80.72.77.07
              </a>
            </div>
          </div>
        </section> */}

        {/* CONTACT */}
        <section
          id="contact-general"
          className="bg-white py-16 sm:py-20 dark:bg-slate-950"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Contact
              </div>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
                Parlons de votre projet de site communal
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                Pour une présentation, une question ou une demande spécifique,
                vous pouvez utiliser le formulaire ci-dessous.
              </p>
            </div>

            <FormContact />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MonAppliMairie;
